"use client";

import MediaSlot from "../media-slot";
import type { MediaSlotProps, BackdropType } from "../media-slot";
export type { BackdropType as BackdropImage } from "../media-slot";
import {
  Accordion,
  Carousel as CarouselPrimitive,
  Link,
  LinkProps,
} from "../../primitives";
import { cn } from "@marketing-sites/utils/cn";
import Image from "next/image";
import type { ReactNode } from "react";

export interface CarouselSlideProps {
  mediaSlot: MediaSlotProps;
  /** Optional overrides applied to `mediaSlot` in the mobile accordion view. */
  mobileMediaSlot?: Partial<MediaSlotProps>;
  /**
   * Per-slide autoplay delay in ms — how long this slide stays before advancing,
   * and the duration of its thumbnail progress timer. Falls back to the carousel's
   * default (7000ms) when omitted. Use to match a slide's Rive animation length.
   */
  autoplayDelay?: number;
  thumbnailMetadata: { analyticsId: string };
  accordionMetadata: { analyticsId: string };
  thumbnail: {
    title: ReactNode;
    description: ReactNode;
    /**
     * Mobile accordion copy. Both views stay in the DOM, so a description with
     * inline links needs this to give each view's links distinct analytics IDs.
     */
    mobileDescription?: ReactNode;
    link?: LinkProps;
  };
}

export type CarouselProps = {
  orientation?: "horizontal" | "horizontalRtl" | "vertical";
  preload?: boolean;
  headingLevel?: "h3" | "h4";
  backdrop?: BackdropType;
  slides: CarouselSlideProps[];
  accentClassName?: string;
  thumbnailClassName?: string;
  /**
   * Typography scale for the slide descriptions. Defaults to `text-body`;
   * pass e.g. `text-body-sm` to opt a specific page's carousels into a smaller
   * description size without changing the default for every other consumer.
   */
  descriptionClassName?: string;
  /** Spacing below the mobile accordion title. Defaults to `mb-6`. */
  mobileTitleSpacingClassName?: string;
  /**
   * Vertical/RTL carousels stretch the selected thumbnail to fill the media
   * height by default. Set to false to let thumbnails keep their natural
   * height and top-align, leaving empty space below the last card.
   */
  stretchThumbnailsToMedia?: boolean;
  autoplayDelay?: number;
  /**
   * Per-slide autoplay delays in ms, indexed by slide position. Takes
   * precedence over a slide's own `autoplayDelay` and the carousel-wide
   * default. Entries may be sparse/omitted — a missing index falls back to
   * the slide's `autoplayDelay`, then the carousel default. Use to time each
   * slide to its Rive's length from one place.
   */
  autoplayDelays?: number[];
};

// Thin wrapper around MediaSlot that injects eager/lazy loading based on the
// carousel's warm-index state. Must be rendered inside CarouselPrimitive.Slide
// so that useSlide() has access to the slide context.
//
// When the slide's media is a Rive, render `CarouselPrimitive.RiveWebGL`
// instead so playback is driven by Embla's selected-slide events — restarts
// from frame 0 on slide selection, stops on deselection. Pair with the
// per-slide `autoplayDelay` prop on `CarouselSlideProps` to match each Rive's length.
function CarouselSlideMedia({
  mediaSlot,
  index,
}: {
  mediaSlot: MediaSlotProps;
  index: number;
}) {
  const { preload } = CarouselPrimitive.useSlide();
  const loading = preload ? ("eager" as const) : ("lazy" as const);

  const { media } = mediaSlot;

  // Backdrop-only slide: nothing to enrich or swap for a Rive renderer. Reached
  // by Publisher-generated carousels, which have the shared backdrop but no
  // foreground asset yet (WEB-3010) — temporary, see MediaSlotProps.media.
  // The desktop carousel still shows below `lg`; only the accordion squares.
  if (!media) return <MediaSlot {...mediaSlot} keepAspectOnMobile />;

  // Desktop carousel always uses the `large` variant (responsive small/large
  // switching only applies to the mobile accordion fallback below).
  const carouselMedia =
    "small" in media && "large" in media ? media.large : media;

  if (carouselMedia.type === "rive") {
    // Route through MediaSlot via `renderMedia` so the slide keeps its backdrop,
    // aspect-ratio frame, and foreground inset — only the media element is
    // swapped for the carousel-synced WebGL Rive renderer.
    return (
      <MediaSlot
        {...mediaSlot}
        renderMedia={
          <CarouselPrimitive.RiveWebGL
            index={index}
            src={carouselMedia.src}
            fit={carouselMedia.fit}
            align={carouselMedia.align}
            stateMachineName={carouselMedia.stateMachineName}
            artboard={carouselMedia.artboard}
            placeholder={carouselMedia.placeholder}
            staticImage={carouselMedia.staticImage}
            className={carouselMedia.className}
          />
        }
      />
    );
  }

  const enrichedMedia =
    "small" in media && "large" in media
      ? {
          small:
            media.small.type === "image"
              ? { ...media.small, loading }
              : media.small,
          large:
            media.large.type === "image"
              ? { ...media.large, loading }
              : media.large,
        }
      : media.type === "image"
        ? { ...media, loading }
        : media;

  return <MediaSlot {...mediaSlot} media={enrichedMedia} />;
}

export default function Carousel({
  slides,
  backdrop,
  orientation = "horizontal",
  headingLevel: Heading = "h4",
  accentClassName = "bg-helpdesk",
  thumbnailClassName,
  descriptionClassName = "text-body",
  mobileTitleSpacingClassName = "mb-6",
  stretchThumbnailsToMedia = true,
  autoplayDelay = 7000,
  autoplayDelays,
}: CarouselProps) {
  const isVertical = orientation === "vertical";
  const isHorizontalRtl = orientation === "horizontalRtl";

  return (
    <div className="@container">
      {/* Above @4xl container widths, render a carousel pattern */}
      <div className="@4xl:block hidden">
        <CarouselPrimitive.Root
          autoplayOptions={{ delay: autoplayDelay }}
          className={cn((isVertical || isHorizontalRtl) && "flex")}
        >
          <div
            className={cn(
              (isVertical || isHorizontalRtl) && "w-full min-w-0",
              isHorizontalRtl && "order-2",
              "relative",
            )}
          >
            {/* Shared backdrop for carousels where every slide has the same background */}
            {backdrop && (
              <Image
                src={backdrop.src}
                alt={backdrop.alt ?? ""}
                fill
                sizes={backdrop.sizes}
                className="object-cover"
                data-publisher-asset={
                  process.env.PUBLISHER_SANDBOX === "1" ? "locked" : undefined
                }
              />
            )}
            <CarouselPrimitive.Viewport className="relative h-full">
              <CarouselPrimitive.Container
                className={cn(isVertical && "h-full")}
              >
                {slides.map(
                  (
                    { mediaSlot, thumbnail, autoplayDelay: slideDelay },
                    index,
                  ) => (
                    <CarouselPrimitive.Slide
                      key={index}
                      index={index}
                      delay={autoplayDelays?.[index] ?? slideDelay}
                      className={cn(
                        "border-linen relative w-full select-none border",
                      )}
                    >
                      <CarouselSlideMedia mediaSlot={mediaSlot} index={index} />
                    </CarouselPrimitive.Slide>
                  ),
                )}
              </CarouselPrimitive.Container>
            </CarouselPrimitive.Viewport>
          </div>

          <CarouselPrimitive.Thumbnails
            className={cn(
              "flex gap-0",
              isVertical || isHorizontalRtl
                ? "w-2/5 shrink-0 flex-col"
                : "flex-row",
              isHorizontalRtl && "order-1",
              thumbnailClassName,
            )}
          >
            {slides.map(({ thumbnail, thumbnailMetadata }, index) => (
              <div
                key={index}
                className={cn(
                  "group/thumb relative",
                  (isVertical || isHorizontalRtl) && !stretchThumbnailsToMedia
                    ? "flex-none"
                    : "flex-1",
                )}
              >
                <CarouselPrimitive.Thumbnail
                  index={index}
                  // eslint-disable-next-line analytics/id-must-be-static -- each slide in the slides array carries a hardcoded id
                  analyticsId={thumbnailMetadata.analyticsId}
                  className={cn(
                    "data-selected:bg-black/3 data-selected:opacity-100 group peer relative flex h-full w-full cursor-pointer flex-col text-left opacity-60 group-hover/thumb:opacity-80",
                    isVertical
                      ? stretchThumbnailsToMedia
                        ? "data-selected:flex-1 min-h-32 flex-none overflow-hidden"
                        : "flex-none"
                      : isHorizontalRtl
                        ? stretchThumbnailsToMedia
                          ? "data-selected:flex-1 flex-none overflow-hidden"
                          : "flex-none"
                        : "flex-1",
                  )}
                  progress={({ ref, isPlaying, isSelected }) => (
                    <div
                      ref={ref}
                      className={cn(
                        "absolute left-0 top-0 h-0.5 w-full origin-left",
                        accentClassName,
                      )}
                      style={{
                        animation: "carouselAutoplay linear",
                        opacity: isPlaying && isSelected ? 1 : 0,
                      }}
                    />
                  )}
                >
                  <div
                    className={cn(
                      "flex h-full gap-4 p-6 lg:gap-6",
                      thumbnail.link && "pb-12",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "mt-1 block size-2 shrink-0",
                        accentClassName,
                      )}
                    />
                    <div className="flex flex-col">
                      <Heading className="text-heading-6 flex pb-2">
                        {thumbnail.title}
                      </Heading>

                      <div
                        className={cn(
                          descriptionClassName,
                          "font-sans! font-normal! text-current/70 space-y-3",
                        )}
                      >
                        <p>{thumbnail.description}</p>
                      </div>
                    </div>
                  </div>
                </CarouselPrimitive.Thumbnail>

                {thumbnail.link && (
                  // Margin matches the text column: p-6 (24px) + bullet size-2 (8px) + gap-4/lg:gap-6 = 48px/56px.
                  <div className="peer-data-selected:opacity-100 text-body font-sans! font-normal! absolute bottom-0 left-0 mb-5 ml-12 text-black/70 opacity-60 group-hover/thumb:opacity-80 lg:ml-14">
                    <Link
                      {...thumbnail.link}
                      className="text-inline-link"
                      analyticsLabel={
                        thumbnail.link.analyticsLabel ?? "Learn more"
                      }
                    >
                      {thumbnail.link.children ?? "Learn more"}
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </CarouselPrimitive.Thumbnails>
        </CarouselPrimitive.Root>
      </div>

      {/* Below @4xl container widths, render an accordion pattern */}
      <div className="@4xl:hidden block">
        <Accordion.Root defaultValue={[0]}>
          {slides.map(
            (
              { mediaSlot, mobileMediaSlot, thumbnail, accordionMetadata },
              index,
            ) => {
              return (
                <Accordion.Item
                  key={index}
                  value={index}
                  className="border-linen data-open:bg-off-white data-open:opacity-100 relative border border-b-0 opacity-60 last:border-b"
                >
                  <Accordion.Header className="group">
                    <Accordion.Trigger
                      // eslint-disable-next-line analytics/id-must-be-static -- each slide in the slides array carries a hardcoded id
                      analyticsId={accordionMetadata.analyticsId}
                      // Description sits outside the trigger, so stretch the hit area over the closed card.
                      className="data-panel-open:after:hidden static flex w-full flex-col px-5 pt-5 after:absolute after:inset-0"
                    >
                      {/* Not a heading: Accordion.Header already renders the
                        semantic heading around this trigger, so a nested one
                        would double-mark the title. */}
                      <span
                        className={cn(
                          "text-heading-5 flex w-full items-start gap-3",
                          mobileTitleSpacingClassName,
                        )}
                      >
                        {/* Group the title (and any inline badge) as one flex
                          item so a badge flows with the text and wraps with it,
                          instead of being stranded next to the +/- toggle. */}
                        <span className="min-w-0 flex-1">
                          {thumbnail.title}
                        </span>
                        {/* Drawn by ::before so the glyph is not text: anything
                          inside the heading is extracted fused to the title by
                          readers that do not apply CSS. */}
                        <span
                          aria-hidden
                          className="group-data-open:before:content-['-'] shrink-0 before:content-['+']"
                        />
                      </span>
                    </Accordion.Trigger>
                  </Accordion.Header>
                  {/* Outside the heading, for the same reason as the toggle. */}
                  <div className="text-current/70 [&_a]:z-1 space-y-2 px-5 pb-5 [&_a]:relative">
                    <p
                      className={cn(
                        descriptionClassName,
                        "font-sans! font-normal! mt-2",
                      )}
                    >
                      {thumbnail.mobileDescription ?? thumbnail.description}
                    </p>
                  </div>
                  <Accordion.Panel className="flex flex-col space-y-5 p-5 pt-0">
                    {thumbnail.link && (
                      <div>
                        <Link
                          {...thumbnail.link}
                          className="text-body font-sans! font-normal! text-inline-link"
                          analyticsLabel={
                            thumbnail.link.analyticsLabel ?? "Learn more"
                          }
                        >
                          {thumbnail.link.children ?? "Learn more"}
                        </Link>
                      </div>
                    )}
                    <MediaSlot
                      {...mediaSlot}
                      {...mobileMediaSlot}
                      backdrop={
                        mobileMediaSlot?.backdrop ??
                        mediaSlot.backdrop ??
                        backdrop
                      }
                    />
                  </Accordion.Panel>
                </Accordion.Item>
              );
            },
          )}
        </Accordion.Root>
      </div>
    </div>
  );
}
